import dayjs, { type Dayjs } from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import type { MessageFragmentFragment } from 'generated/graphql';
import { cleanTagsFromText, decodeEntities } from '~/utils';
import { localizeTime } from '~/modules/root';

dayjs.extend(isToday);

const FILE_ATTACHMENT_MESSAGE_PREVIEW = 'Sent a file attachment';

/**
 * Generate the preview message from a message object.
 * @param message The message object.
 * @param userID The ID of the current user.
 * @returns The preview message.
 */
export function getPreviewMessage(
    message: MessageFragmentFragment,
    userID: number
): string {
    const sender = message.Participant.Identity;
    const senderString =
        sender.__typename === 'Member' && sender.ID === userID
            ? 'You'
            : message.CreatedBy // CreatedBy should always exist
              ? message.CreatedBy.split(' ')[0]
              : '';

    let messageContent = '';
    if (message.__typename === 'MemberEnquiryMessage') {
        if (message.Attachments.length) {
            messageContent = FILE_ATTACHMENT_MESSAGE_PREVIEW;
        } else {
            // May have <br> tags and HTML entities.
            messageContent = decodeEntities(cleanTagsFromText(message.Content));
        }
    } else if (message.__typename === 'FileAttachmentMessage') {
        messageContent = FILE_ATTACHMENT_MESSAGE_PREVIEW;
    } else if (message.__typename === 'TextMessage') {
        if (message.IsHTML) {
            messageContent = decodeEntities(cleanTagsFromText(message.Content));
        } else {
            messageContent = message.Content;
        }
    } else if (message.__typename === 'RevealContactDetailsMessage') {
        // Special case: Do not need to include sender string.
        // XXX has viewed your contact details and ...
        return message.Content;
    }
    return senderString ? `${senderString}: ${messageContent}` : messageContent;
}

/**
 * Format a Dayjs date to a readable string with the full date and time. This
 * is used for the message bubble tooltip.
 * @param date The date to format.
 * @returns The formatted date string.
 */
export function formatDateFull(date: Dayjs): string {
    return date.format('D MMM YYYY, h:mm a');
}

/**
 * Format a Dayjs date to a readable string. Shows the time only if today,
 * otherwise shows both the date and time. This is used in message bubbles.
 * @param date The date to format.
 * @returns The formatted date string.
 */
export function formatDatePartial(date: Dayjs): string {
    if (date.isToday()) {
        return date.format('h:mm a');
    }
    return date.format('D MMM, h:mm a');
}

/**
 * Format a Dayjs date to a readable string for message groups.
 * @param date The date to format.
 * @returns The formatted date string.
 */
function formatMessageGroupDate(date: Dayjs): string {
    if (date.isToday()) {
        return 'Today';
    }
    const today = dayjs();
    if (date.isSame(today.subtract(1, 'day'), 'day')) {
        return 'Yesterday';
    }
    return date.format('D MMMM');
}

interface MessageGroup {
    date: Dayjs;
    dateString: string;
    messages: MessageFragmentFragment[];
}

/**
 * Group a list of conversation messages by date.
 * @param messages The list of messages in descending order of date.
 * @returns The list of message grouped by date.
 */
export function groupMessagesByDate(
    messages: MessageFragmentFragment[]
): MessageGroup[] {
    const groupedMessages: MessageGroup[] = [];
    // Assume the messages are in descending order of date.
    const messagesSorted = [...messages].reverse();
    for (const message of messagesSorted) {
        const date = localizeTime(message.Created);
        const dateString = formatMessageGroupDate(date);
        const currentGroup = groupedMessages.length
            ? groupedMessages[groupedMessages.length - 1]
            : undefined;
        if (currentGroup && currentGroup.dateString === dateString) {
            currentGroup.messages.push(message);
        } else {
            groupedMessages.push({
                date,
                dateString,
                messages: [message],
            });
        }
    }
    return groupedMessages;
}
